import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import templateApi from '../../api/templateApi';
import { resolveAssetUrl } from '../../api/adminAlertTemplate.api';
import './alert-templates.css';

const TABS = [
  { id: 'all', label: 'ALL' },
  { id: 'templates', label: 'TEMPLATES' },
  { id: 'ready', label: 'READY TO SEND' },
];

function TemplateCard({ item, onEdit, onClone, onDelete }) {
  const preview = resolveAssetUrl(item.previewurl);
  return (
    <div className="at-card">
      <div className="at-card-preview">
        {preview ? (
          <img src={preview} alt={item.name} />
        ) : (
          <div className="at-card-placeholder">No preview</div>
        )}
      </div>
      <div className="at-card-body">
        <h3>{item.name}</h3>
        <span className={`at-badge at-badge-${item.status}`}>{item.status}</span>
        <div className="at-card-actions">
          <button type="button" className="at-btn at-btn-sm" onClick={() => onEdit(item.id)}>Edit</button>
          <button type="button" className="at-btn at-btn-sm at-btn-secondary" onClick={() => onClone(item)}>Clone</button>
          <button type="button" className="at-btn at-btn-sm at-btn-danger" onClick={() => onDelete(item.id)}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function AlertTemplateList() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const all = await templateApi.listAll();
      setTemplates(all);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const displayed = useMemo(() => {
    if (activeTab === 'templates') {
      return templates.filter((t) => t.status === 'template');
    }
    if (activeTab === 'ready') {
      return templates.filter((t) => t.status === 'ready');
    }
    return templates;
  }, [activeTab, templates]);

  const handleCreate = async () => {
    try {
      const created = await templateApi.createNew('New Alert Template');
      navigate(`/alert-templates/${created.id}/edit`);
    } catch (err) {
      setError(err.message || 'Failed to create template');
    }
  };

  const handleEdit = (id) => navigate(`/alert-templates/${id}/edit`);

  const handleClone = async (item) => {
    try {
      const cloned = await templateApi.clone(item);
      navigate(`/alert-templates/${cloned.id}/edit`);
    } catch (err) {
      setError(err.message || 'Clone failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await templateApi.deleteTemplate(id);
      load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  return (
    <div className="at-page">
      <PageHeader
        title="Message Templates"
        icon="fa-palette"
        actions={(
          <button type="button" className="at-btn at-btn-primary" onClick={handleCreate}>
            + Create New
          </button>
        )}
      />

      {error && <div className="at-error">{error}</div>}

      <div className="at-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`at-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p>Loading templates…</p>
      ) : (
        <div className="at-grid">
          <div className="at-create-card" onClick={handleCreate} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleCreate()}>
            <span className="at-create-icon">+</span>
            <p>Create New Alert Template</p>
          </div>
          {displayed.map((item) => (
            <TemplateCard
              key={item.id}
              item={item}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
